import { useEffect } from 'react';
import { useLatest } from './useLatest';

/**
 * useOnClickOutside will call `fn(false)` whenever the user clicks outside
 * the provided element (and will call `fn(true)` whenever the user clicks inside
 * the provided element)
 */
export function useOnClickInsideOutside(
  element: React.RefObject<Node | null | undefined>,
  fn: (isInside: boolean) => void,
  options: {
    disabled?: boolean;
  } = {},
  triggerElement?: React.RefObject<Node | null | undefined>,
) {
  const latestFn = useLatest(fn);
  const disabled = options.disabled || false;

  useEffect(() => {
    if (!disabled) {
      const listener = (e: MouseEvent) => {
        let clickTarget: Node | null = isNode(e.target) ? e.target : null;

        /*
         * Don't do anything if one of the following conditions are true
         * 1. The current element is not defined
         * 2. The click target is the trigger element or a child
         * This is necessary in PopoverTooltip because without it,
         * The listener function will be called immediately to dismiss PopoverTooltip
         */
        if (!element.current || triggerElement?.current?.contains(clickTarget)) {
          return;
        }

        let clickInside = false;
        while (clickTarget) {
          if (clickTarget === element.current) {
            clickInside = true;
            break;
          }
          clickTarget = clickTarget.parentNode;
        }

        latestFn.current(clickInside);
      };

      document.addEventListener('mousedown', listener);
      return () => document.removeEventListener('mousedown', listener);
    }
  }, [element, latestFn, disabled]);
}

function isNode(n: EventTarget | null): n is Node {
  return 'parentNode' in (n as any);
}
